import React, { useEffect, useRef, useState } from 'react';
import { FormControlLabel, Switch } from '@mui/material';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSnackbar } from 'material-ui-snackbar-provider';

import Modal from '../../modules/Modal';
import styles from './DeleteOrganizationModal.module.scss';
import { deleteOrganizationById, restoreOrganizationById } from '../../../api/organizationsApi';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { saveUser } from '../../../store/user/userSlice';
import { getMyUserInfo } from '../../../api/usersApi';

type TDeleteOrganizationModal = {
  id: string;
  open: boolean;
  switchModal: () => void;
  fetchAll?: () => void;
  restore?: boolean;
};

const DeleteOrganizationModal: React.FC<TDeleteOrganizationModal> = ({ id, open, switchModal, fetchAll, restore = false }) => {
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const [loading, setLoading] = useState(false);
  const [moveToTrash, setMoveToTrash] = useState(true);
  useEffect(() => { if (open) setMoveToTrash(true); }, [open, id]);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const handleDelete = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await (restore ? restoreOrganizationById(id) : deleteOrganizationById(id, moveToTrash));
      if (restore) {
        try {
          const user = await getMyUserInfo(true);
          dispatch(saveUser(user.data));
        } catch (err) {
          console.error('Could not refresh organisations after restore', err);
        }
      } else if (currentUser?.organizations) {
        dispatch(
          saveUser({
            ...currentUser,
            organizations: currentUser.organizations.filter(
              (organization) => organization.id.toString() !== id.toString(),
            ),
          }),
        );
      }
      await fetchAll?.();
      switchModal();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      } else {
        notification.showMessage(restore ? 'Could not restore the organisation.' : 'Could not archive the organisation.');
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    if (!loading) {
      switchModal();
    }
  };

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={closeModal} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>{restore ? 'Restore this organisation?' : 'Archive this organisation?'}</div>
        <p>
          {restore
            ? 'The organisation and its existing access will become active again. Content moved to Trash stays there. Files that have already expired cannot be restored.'
            : 'Access will be disabled. Organisation records can be restored at any time.'}
        </p>
        {!restore && <>
          <FormControlLabel className={styles.trashOption}
            control={<Switch checked={moveToTrash} onChange={(event) => setMoveToTrash(event.target.checked)} disabled={loading}
              inputProps={{ role: 'switch', 'aria-describedby': 'organization-archive-retention' }} />}
            label="Move all videofeeds and folders to Trash" />
          <p>When switched off, restoring within 30 days returns content to its original locations.</p>
          <p id="organization-archive-retention">
            Video files are permanently deleted 30 days after archiving. Restoring the organisation after 30 days
            {' '}places all content in Trash, regardless of this switch. Switching it off does not extend file retention.
          </p>
          <p>Find Trash in File Management under Deleted folders and Deleted videofeeds.</p>
        </>}
        <div className={styles.form}>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={handleDelete} kind="default" disabled={loading}>
              <div ref={submitButtonRef}>{restore ? 'Restore organisation' : 'Archive organisation'}</div>
            </OutlinedButton>

            <ContainedButton kind="purple" onClick={closeModal} disabled={loading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteOrganizationModal;
